import React from 'react';

export enum PageAlertVariant {
  BRAND = 'brand',
  ERROR = 'error',
  SUCCESS = 'success',
  INFO = 'info',
}

interface PageAlertProps {
  variant?: PageAlertVariant;
  headline?: string;
  body?: string; // content editor only cares about a text, so not adding a ReactNode type here
}

const variantStyles = {
  [PageAlertVariant.BRAND]: {
    container: 'bg-alert-brand-bg border-primary',
    headlineText: 'text-primary',
  },
  [PageAlertVariant.ERROR]: {
    container: 'bg-alert-error-bg border-alert-error',
    headlineText: 'text-alert-error',
  },
  [PageAlertVariant.SUCCESS]: {
    container: 'bg-alert-success-bg border-alert-success',
    headlineText: 'text-alert-success',
  },
  [PageAlertVariant.INFO]: {
    container: 'bg-alert-info-bg border-alert-info',
    headlineText: 'text-alert-info',
  },
} as const;

export const PageAlert: React.FC<PageAlertProps> = ({ variant = PageAlertVariant.BRAND, headline, body }) => {
  // fallback to BRAND if an invalid variant is passed
  const styles = variantStyles[variant] ?? variantStyles[PageAlertVariant.BRAND];

  if (!headline && !body) {
    return null;
  }

  return (
    <div
      className={`w-full rounded-[10px] border-2 px-4 py-6 ${styles.container}`}
      role="alert"
      aria-live={variant === PageAlertVariant.ERROR ? 'assertive' : 'polite'}
      aria-atomic="true"
    >
      <div className="flex flex-col gap-4">
        {headline && <p className={`m-0 text-[16px] leading-[1.4] font-bold ${styles.headlineText}`}>{headline}</p>}
        {body && <p className="text-dark m-0 text-[16px] leading-[1.4]">{body}</p>}
      </div>
    </div>
  );
};
